import { modifier } from 'ember-modifier';
import isRemovedString from 'garaje/helpers/is-removed-string';
import { SENSITIVE_RESPONSE_STRINGS } from 'garaje/utils/enums';

const ITALIC_CLASS = 'italic';

interface ItalicizeIfRemovedSignature {
  Args: {
    Positional: [value?: string | null];
  };
  Element: HTMLElement;
}

/**
 * Modifier to italicize a display element whose text represents a "removed" value.
 *
 * Unlike {{handle-removed-string}}, this modifier only toggles a styling class — it never
 * mutates text content. Text replacement for these elements is handled declaratively by the
 * {{removed-string}} helper, so this is safe to apply to Glimmer-owned text nodes without
 * fighting reactivity.
 *
 * Also italicizes a masked sensitive field (the [HIDDEN] wire sentinel, rendered as "Hidden"
 * by {{removed-string}}). This is intentionally a direct sentinel check rather than widening
 * `is-removed-string`, which also drives {{handle-removed-string}}'s "value was deleted"
 * aria-label + input-disable semantics that must not apply to masked fields. See VIS-17460.
 */
export default modifier<ItalicizeIfRemovedSignature>((element, [value]) => {
  // Computed before the isRemovedString guard narrows `value`, so the sentinel
  // comparison isn't flagged as a no-overlap type error. See VIS-17460.
  const isMaskedSentinel = value === SENSITIVE_RESPONSE_STRINGS.PLACEHOLDER;
  if (!isRemovedString(value) && !isMaskedSentinel) return;

  const hadItalicClass = element.classList.contains(ITALIC_CLASS);
  element.classList.add(ITALIC_CLASS);

  return () => {
    if (!hadItalicClass) {
      element.classList.remove(ITALIC_CLASS);
    }
  };
});
